'use client';

import { NumberInput, PatternInput } from '@lulalife/design-system';
import get from 'lodash/get';
import { useController, useFormContext } from 'react-hook-form';

type CustomNumberFieldProps = {
  name: string;
  type: 'number' | 'pattern';
  label?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  format?: string;
  hideFormError?: boolean;
  /** Gates only the invalid-state highlight, independent of `hideFormError` — same split the legacy `'number'` mode made. Defaults to `true`. */
  showErrorHighLight?: boolean;
  /** Clears the field's validation error on every value change, same as the legacy `clearError`. */
  clearError?: boolean;
  /** Caps the allowed numeric value, same as the legacy `maxLimit` (`'number'` mode only). */
  maxLimit?: number;
  allowNegative?: boolean;
  decimalScale?: number;
  fixedDecimalScale?: boolean;
  thousandSeparator?: boolean;
  prefix?: string;
  suffix?: string;
  /** Overrides the displayed value without changing what's stored — same as the legacy `customValue`. */
  customValue?: string | number | ((value: string) => string);
  /**
   * A TRANSFORM, not a side-effect callback — its return value is what gets
   * stored on the field, same as the legacy `CustomNumberField`. `'number'`
   * mode only; `'pattern'` mode never wired this up in the legacy component
   * either.
   */
  customHandleChange?: (value: string) => unknown;
  mandatory?: boolean;
  'data-testid'?: string;
};

/**
 * Same `name`-bound API as `src/components/Shared/FormFields/CustomNumberField.tsx`
 * for its `'pattern'`/`'number'` modes — only the rendered control changed,
 * from a reactstrap `Input` to the design system's `NumberInput` / `PatternInput`.
 * This wrapper only binds them to react-hook-form. A call site swaps the import
 * and keeps every prop it already passes for a masked field. (`prefix` / `suffix`
 * render in the DS field's own prefix / suffix slots, not inside the typed text.)
 *
 * Deliberately not carried over: the old component's `'incrementor'` mode (a
 * hand-rolled +/- stepper with no masking involved, used only in the
 * style-guide) and `readOnly` (zero real call sites pass it) — neither has a
 * design-system counterpart to render through or a real consumer to preserve.
 */
export const CustomNumberField = (props: CustomNumberFieldProps) => {
  const {
    name,
    type,
    label,
    className = '',
    placeholder = '',
    disabled = false,
    format = '',
    hideFormError = false,
    showErrorHighLight = true,
    clearError = false,
    maxLimit,
    allowNegative = true,
    decimalScale = 0,
    fixedDecimalScale,
    thousandSeparator = false,
    prefix,
    suffix,
    customValue,
    customHandleChange,
    mandatory = false,
    'data-testid': dataTestId,
  } = props;

  const { control, clearErrors } = useFormContext();
  const {
    field,
    formState: { errors },
  } = useController({ name, control });

  const fieldError = get(errors, name);
  const fieldErrorMessage =
    typeof fieldError?.message === 'string' ? fieldError.message : undefined;
  const resolvedLabel = mandatory && label ? `${label} *` : label;

  const displayValue = (() => {
    if (customValue == null) return field.value;
    return typeof customValue === 'function' ? customValue(field.value) : customValue;
  })();

  if (type === 'number') {
    return (
      <NumberInput
        id={name}
        label={resolvedLabel}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        error={hideFormError ? false : Boolean(fieldError) && showErrorHighLight}
        errorMessage={hideFormError ? undefined : fieldErrorMessage}
        data-testid={dataTestId ?? `${name}-number-input`}
        value={displayValue}
        decimalScale={decimalScale}
        fixedDecimalScale={fixedDecimalScale ?? false}
        thousandSeparator={thousandSeparator}
        allowNegative={allowNegative}
        prefix={prefix ?? ''}
        suffix={suffix ?? ''}
        max={maxLimit || undefined}
        onValueChange={(value) => {
          if (customHandleChange) {
            field.onChange(customHandleChange(value));
          } else {
            field.onChange(value || null);
          }
          // No `name` arg, matching the legacy component's own (broad) call —
          // this clears every field's error, not just this one.
          if (clearError) clearErrors();
        }}
        onBlur={field.onBlur}
      />
    );
  }

  return (
    <PatternInput
      id={name}
      label={resolvedLabel}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      error={hideFormError ? false : Boolean(fieldError)}
      errorMessage={hideFormError ? undefined : fieldErrorMessage}
      data-testid={dataTestId ?? `${name}-number-input`}
      value={field.value}
      format={format}
      prefix={prefix}
      suffix={suffix}
      onValueChange={(value) => {
        field.onChange(value);
        // No `name` arg, matching the legacy component's own (broad) call —
        // this clears every field's error, not just this one.
        if (clearError) clearErrors();
      }}
      onBlur={field.onBlur}
    />
  );
};

export default CustomNumberField;
